<template>
  <AccountCard :title="$t('pages.account.settings.sections.delivery_address.title')">
    <hr class="my-6" />
    <div class="flex flex-row flex-wrap items-center gap-4">
      <VForm
        ref="formRef"
        :initial-values="initialValues"
        :validation-schema="Schema"
        v-slot="{ handleSubmit }"
        as="div"
        class="min-w-[15rem] flex-1"
      >
        <form @submit="handleSubmit($event, mutate)" class="max-w-lg">
          <CommonAlert v-if="error" type="error" :content="error.message" class="mb-6 mt-4" />
          <div class="mt-2 w-full">
            <div class="flex flex-row flex-wrap gap-x-2 md:gap-y-6 md:pt-4">
              <ClientOnly>
                <BaseVSelect
                  name="country"
                  :label="$t('form.country')"
                  :options="countries"
                  class="flex-1 basis-full"
                  :placeholder="`${$t('form.country')}`"
                  @onSelected="onCountryChanged"
                />
              </ClientOnly>

              <CommonAddressFinder
                name="google_autocomplete"
                class="flex-1 basis-full"
                :hidden="manualAddress"
                :label="`${$t('form.searchLocation')}`"
                :country="getCountryCode(country)"
                :countryCode="getCountryCode3(country)"
                :modelValue="addressAutocomplete"
                @blur="handleAutocompleteBlur"
                @update:placeSelected="(place: AutocompletePlace) => handlePlaceSelected(place)"
                @update:modelValue="handleInput"
              />

              <!--  <CommonGoogleAutocomplete
                :hidden="manualAddress"
                name="google_autocomplete"
                :label="`${$t('form.searchLocation')}`"
                :country="getCountryCode(country)"
                :modelValue="googleAutocomplete"
                class="flex-1 basis-full"
                @blur="handleAutocompleteBlur"
                @update:placeSelected="(place: AutocompletePlace) => handlePlaceSelected(place)"
                @update:modelValue="handleInput"
              /> -->

              <div v-show="manualAddress" class="flex flex-row flex-wrap gap-x-2 md:gap-y-6 md:pt-4">
                <BaseVTextInput
                  v-show="getFieldRules(country).address_line_1.visible"
                  type="text"
                  :label="$t('form.address1')"
                  name="address_line_1"
                  class="flex-1 basis-full"
                />
                <BaseVTextInput
                  v-show="getFieldRules(country).address_line_2.visible"
                  type="text"
                  :label="$t('form.address2')"
                  name="address_line_2"
                  class="flex-1 basis-full"
                />
                <BaseVTextInput
                  v-show="getFieldRules(country).city.visible"
                  type="text"
                  :label="$t('form.city')"
                  name="city"
                  class="flex-1 basis-1/3"
                />
                <BaseVTextInput
                  v-show="getFieldRules(country).state.visible"
                  type="text"
                  :label="getFieldRules(country).state.required ? $t('form.state') : $t('form.stateOptional')"
                  name="state"
                  class="flex-1 basis-1/3"
                />
                <BaseVTextInput
                  v-show="getFieldRules(country).postcode.visible"
                  type="text"
                  :label="getFieldRules(country).postcode.required ? $t('form.postcode') : $t('form.postcodeOptional')"
                  name="postcode"
                  class="flex-1 basis-1/3"
                />
              </div>
            </div>

            <div class="flex flex-row flex-wrap gap-x-2 md:gap-y-6 md:pt-4">
              <NuxtLinkLocale
                class="cursor-pointer text-center"
                :locale="switchBlogLocale()"
                @click="toggleManualAddress"
              >
                <span class="text-primary whitespace-nowrap underline ml-2" v-if="!manualAddress">
                  {{ $t("pages.account.address.manual_change") }}
                </span>
                <span class="text-primary whitespace-nowrap underline ml-2" v-else>
                  {{ $t("pages.account.address.find") }}
                </span>
              </NuxtLinkLocale>
            </div>

            <div class="mt-8 flex flex-row flex-wrap items-center gap-4">
              <BaseButton
                type="submit"
                class="w-auto max-w-full whitespace-nowrap text-[1rem]"
                :loading="pending"
                size="medium"
              >
                {{ $t("pages.account.buttons.save_changes") }}
              </BaseButton>

              <div class="flex-1 shrink-0 basis-1/3 text-primary">
                <div class="flex flex-row items-center gap-2" v-if="success">
                  <CheckCircleIcon class="h-[1.5em] w-[1.5em] shrink-0" />
                  <span>{{ $t("pages.account.texts.changes_saved") }}</span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </VForm>

      <div class="flex-grow basis-60 rounded-md bg-[#D8D8D84D] p-4 text-sm md:flex-grow-0">
        {{ $t("pages.account.address.home_test_note") }}
      </div>
    </div>
  </AccountCard>
</template>

<script setup lang="ts">
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { AxiosError } from "axios";
import * as yup from "yup";
import { switchBlogLocale } from "~/composables/utils";
import AccountCard from "../components/account-card.vue";
import { useAddressControls } from "~/composables/order-processing/useAddressControls";

import type { User } from "~/entities/User";
import type { AutocompletePlace } from "~/components/common/AddressFinder.vue";
import type { Country } from "~/types/order-processing";

interface AccountSettingsPersonalDetailsProps {
  profileDetails: User.ProfileDetails;
}
const props = defineProps<AccountSettingsPersonalDetailsProps>();

const globalStore = useGlobalStore();
const { t } = useI18n();

const localProfile = reactive({ ...props.profileDetails }); // Create reactive local copy
const { getFieldRules } = useAddressControls();

const { data: countriesData } = await getCountries({ type: "home-test-kit" });
const countries = computed(() =>
  (countriesData.value?.data ?? ([] as Country[])).map((country: Country) => ({
    text: country.title,
    value: country.slug,
  }))
);

const createSchema = (country: string, manualAddress: boolean) => {
  const rules = getFieldRules(country);

  return yup.object({
    type: yup.string().default("address"),

    google_autocomplete: yup.string().when([], {
      is: () => manualAddress === false,
      then: (schema) =>
        schema
          .required(t("pages.account.address.use_autocomplete"))
          .test("valid-autocomplete", t("validationErrors.validAddressSelection"), function (value) {
            const { address_line_1 } = this.parent;
            // If user typed something but the address lines didn't populate
            if (value && !address_line_1) {
              return false;
            }
            return true;
          }),
      otherwise: (schema) => schema.optional(),
    }),

    address_line_1: yup.string().when([], {
      is: () => manualAddress === true,
      then: (schema) =>
        rules.address_line_1.required ? schema.required(t("validationErrors.address1Required")) : schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),

    address_line_2: yup.string().optional(),

    city: yup.string().when([], {
      is: () => manualAddress === true,
      then: (schema) => (rules.city.required ? schema.required(t("validationErrors.cityRequired")) : schema.optional()),
      otherwise: (schema) => schema.optional(),
    }),

    state: yup.string().when([], {
      is: () => manualAddress === true,
      then: (schema) =>
        rules.state.required ? schema.required(t("validationErrors.stateRequired")) : schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),

    postcode: yup.string().when([], {
      is: () => manualAddress === true,
      then: (schema) =>
        rules.postcode.required ? schema.required(t("validationErrors.postcodeRequired")) : schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),

    country: yup.string().required(t("validationErrors.countryRequired")),
  });
};
const Schema = computed(() => createSchema(country.value as string, manualAddress.value as boolean));

type Schema = yup.InferType<typeof Schema>;

const initialValues = computed(() => ({
  type: "address",
  google_autocomplete: localProfile.google_autocomplete ?? "",
  address_line_1: localProfile.address_line_1 ?? "",
  address_line_2: localProfile.address_line_2 ?? "",
  city: localProfile.city ?? "",
  state: localProfile.state ?? "",
  postcode: localProfile.postcode ?? "",
  country: localProfile.country ?? "",
}));

const formRef = ref<any>(null);

const country = ref<string>(localProfile.country as string);
const manualAddress = ref(true);
const addressAutocomplete = ref("");

const toggleManualAddress = () => {
  manualAddress.value = !manualAddress.value;
};

const getCountryCode = (slug: string) => {
  const country = (countriesData.value?.data ?? ([] as Country[])).find((c: Country) => c.slug === slug);
  return country?.code ?? "";
};

const getCountryCode3 = (slug: string) => {
  const country = (countriesData.value?.data ?? ([] as Country[])).find((c: Country) => c.slug === slug);
  return country?.code3 ?? "";
};

const onCountryChanged = async (value: string) => {
  country.value = value;
  await clearFields();
};

const handleInput = (value: string) => {
  addressAutocomplete.value = value;
};

let selectedAddress = localProfile.google_autocomplete ?? "";
const handleAutocompleteBlur = (event: FocusEvent) => {
  const value = (event.target as HTMLInputElement).value;

  addressAutocomplete.value = value;

  if (value.trim() !== selectedAddress.trim()) {
    clearFields(false);
  }
};

const handlePlaceSelected = async (place: AutocompletePlace) => {
  if (!place) {
    await clearFields(false);
    console.warn("No place data found");
    return;
  }

  if (!formRef.value) return;

  const rules = getFieldRules(country.value as string);

  const payload: Record<string, any> = {
    google_autocomplete: addressAutocomplete.value || "",
  };

  if (rules.address_line_1.visible) {
    payload.address_line_1 = place?.address_line_1 || "";
  }

  if (rules.address_line_2.visible) {
    payload.address_line_2 = place?.address_line_2 || "";
  }

  if (rules.city.visible) {
    payload.city = place?.city || "";
  }

  if (rules.state.visible) {
    payload.state = place?.state || "";
  }

  if (rules.postcode.visible) {
    payload.postcode = place?.postal_code || "";
  }

  formRef.value.setValues(payload);
  selectedAddress = addressAutocomplete.value || "";
};

const clearFields = async (shouldClearAutocomplete = true) => {
  if (!formRef.value) return;

  // Reset all fields
  formRef.value.setValues({
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    postcode: "",
  });

  if (shouldClearAutocomplete) {
    formRef.value.setValues({
      google_autocomplete: "",
    });
  }
};

watch(
  () => country.value,
  async (newCountry, oldCountry) => {
    if (newCountry && newCountry !== oldCountry) {
      await clearFields();
    }
  }
);

const error = ref<Error>();
const credentialsCopy = reactive({});

const { mutate, pending, success } = useMutation({
  mutationFn: async (credentials: Schema) => {
    Object.assign(credentialsCopy, credentials);
    return useAxios()
      .put("/profile/update", credentials)
      .then((res) => res.data);
  },
  onError(err) {
    error.value = err;
    if (err instanceof AxiosError) error.value = new Error(err.response?.data.message);
  },
  onSuccess() {
    error.value = undefined;
    Object.assign(localProfile, credentialsCopy);
    delete localProfile.type;

    const profileStore = useProfileStore();
    profileStore.setProfile(localProfile);
  },
});
</script>
